import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-form";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { PasswordInput } from "@/components/PasswordInput";
import { Select } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import {
    SearchableMultiSelect,
    MultiSelectOption,
} from "@/components/SearchableMultiSelect";
import { Password, PasswordFormData, PasswordGroup } from "@/types/password";
import { authApi, passwordGroupApi, passwordUserGroupApi } from "@/lib/api";
import { Icon } from "@/components/Icon";

export interface PasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: PasswordFormData) => Promise<void>;
    password?: Password | null;
}

export function PasswordModal({
    isOpen,
    onClose,
    onSave,
    password,
}: PasswordModalProps) {
    const { t } = useTranslation("global");
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<MultiSelectOption[]>([]);
    const [groups, setGroups] = useState<{ value: number; label: string }[]>(
        []
    );
    const [pugs, setPugs] = useState<any[]>([]);

    const [isNewGroupModalOpen, setIsNewGroupModalOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [creatingGroup, setCreatingGroup] = useState(false);

    const [formData, setFormData] = useState<PasswordFormData>({
        title: "",
        group_id: null,
        username: "",
        password: "",
        url: "",
        notes: "",
        shared_with: [],
    });

    const loadGroups = async () => {
        try {
            const res = await passwordGroupApi.getAll();
            setGroups(
                res.map((g: PasswordGroup) => ({ value: g.id, label: g.name }))
            );
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            loadGroups();
            passwordUserGroupApi.getAll().then(setPugs).catch(console.error);
            if (password) {
                setFormData({
                    title: password.title,
                    group_id: password.group_id || null,
                    username: password.username || "",
                    password: "", // Leave empty when editing unless changing
                    url: password.url || "",
                    notes: password.notes || "",
                    shared_with: password.shared_with?.map((u) => u.id) || [],
                    shared_password_user_group_ids:
                        password.shared_with_password_user_groups?.map(
                            (g) => g.id
                        ) || [],
                });
            } else {
                setFormData({
                    title: "",
                    group_id: null,
                    username: "",
                    password: "",
                    url: "",
                    notes: "",
                    shared_with: [],
                    shared_password_user_group_ids: [],
                });
            }
        }
    }, [isOpen, password]);

    const handleCreateGroup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newGroupName.trim()) return;
        setCreatingGroup(true);
        try {
            const newGroup = await passwordGroupApi.create({
                name: newGroupName,
            });
            setGroups([
                ...groups,
                { value: newGroup.id, label: newGroup.name },
            ]);
            setFormData({ ...formData, group_id: newGroup.id });
            setIsNewGroupModalOpen(false);
            setNewGroupName("");
        } catch (e) {
            console.error(e);
        } finally {
            setCreatingGroup(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await authApi.users();
            if (res.result && res.data) {
                setUsers(
                    res.data.map((u: any) => ({
                        id: u.id,
                        name: u.name,
                        subtitle: u.email,
                    }))
                );
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setLoading(true);
        try {
            await onSave(formData);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={password ? t("passwords.edit") : t("passwords.new")}
                maxWidth="lg"
                onSubmit={handleSubmit}
                isSubmitting={loading}
                submitDisabled={
                    loading ||
                    !formData.title ||
                    (!password && !formData.password)
                }
            >
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="w-full">
                        <Input
                            label={t("passwords.form.title")}
                            value={formData.title}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    title: e.target.value,
                                })
                            }
                            required
                        />
                    </div>

                    <div className="w-full">
                        <SearchableMultiSelect
                            label={t("passwords.form.group")}
                            options={[
                                { id: 0, name: "General (Sin grupo)" },
                                ...groups.map((g) => ({
                                    id: g.value,
                                    name: g.label,
                                })),
                            ]}
                            value={
                                formData.group_id ? [formData.group_id] : [0]
                            }
                            onChange={(val) => {
                                if (!val.length || val[0] === 0) {
                                    setFormData({
                                        ...formData,
                                        group_id: null,
                                    });
                                } else {
                                    setFormData({
                                        ...formData,
                                        group_id: val[0],
                                    });
                                }
                            }}
                            placeholder={t("passwords.form.group")}
                            singleSelect={true}
                            onAdd={() => setIsNewGroupModalOpen(true)}
                        />
                    </div>

                    <div className="w-full">
                        <Input
                            label={t("passwords.form.username")}
                            value={formData.username}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    username: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="w-full">
                        <label className="block text-xs font-bold tracking-wider text-slate-600 mb-1">
                            {t("passwords.form.password")}
                        </label>
                        <PasswordInput
                            value={formData.password}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    password: e.target.value,
                                })
                            }
                            required={!password} // Required only on creation
                            placeholder={
                                password
                                    ? t(
                                          "passwords.form.password_placeholder_edit"
                                      )
                                    : ""
                            }
                        />
                    </div>

                    <div className="w-full">
                        <Input
                            label={t("passwords.form.url")}
                            value={formData.url}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    url: e.target.value,
                                })
                            }
                            type="url"
                        />
                    </div>

                    <div className="w-full">
                        <Textarea
                            label={t("passwords.form.notes")}
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    notes: e.target.value,
                                })
                            }
                            rows={3}
                        />
                    </div>

                    <div className="w-full">
                        <SearchableMultiSelect
                            label={t("passwords.form.shared_with")}
                            options={users}
                            value={formData.shared_with || []}
                            onChange={(val: number[]) =>
                                setFormData({ ...formData, shared_with: val })
                            }
                            placeholder={t("passwords.form.shared_placeholder")}
                            className="w-full"
                        />
                    </div>

                    <div className="w-full">
                        <SearchableMultiSelect
                            label={t("passwords.groups.share_with_user_groups")}
                            options={
                                pugs?.map((p: any) => ({
                                    id: p.id,
                                    name: p.name,
                                })) || []
                            }
                            value={
                                formData.shared_password_user_group_ids || []
                            }
                            onChange={(val: number[]) =>
                                setFormData({
                                    ...formData,
                                    shared_password_user_group_ids: val,
                                })
                            }
                            placeholder={t("passwords.user_groups.title")}
                            className="w-full"
                        />
                    </div>
                </form>
            </Modal>

            {isNewGroupModalOpen && (
                <Modal
                    isOpen={isNewGroupModalOpen}
                    onClose={() => setIsNewGroupModalOpen(false)}
                    title={t("passwords.form.new_group_title")}
                    onSubmit={handleCreateGroup}
                    isSubmitting={creatingGroup}
                    submitDisabled={!newGroupName.trim() || creatingGroup}
                    maxWidth="sm"
                >
                    <div className="pt-2">
                        <Input
                            label={t("passwords.form.group_name")}
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>
                </Modal>
            )}
        </>
    );
}
